import { faTriangleExclamation } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type { FieldErrors } from "react-hook-form";
import type { Form } from "../../apiClient/generated";

const FIELD_LABELS: Record<string, string> = {
    responsible: "Assignee",
    reviewer: "Coordinator",
};

interface Props {
    forms: Form[];
    errors: FieldErrors;
}

export const ValidationErrorSummary = ({ forms, errors }: Props) => {
    // Computed on every render on purpose: react-hook-form mutates the
    // `errors` object in place on setError() (e.g. server-side errors), so
    // its reference is not a reliable memo dependency.
    const errorMessages = (() => {
        const messages: string[] = [];

        const formFieldLabels = Object.fromEntries(
            forms.flatMap((form) =>
                form.fields.map((field) => [field.fieldName, field.label]),
            ),
        );

        if (!errors) {
            return [];
        }
        const extractMessages = (fieldError: any): string[] => {
            if (!fieldError) return [];
            if (fieldError.types) {
                return Object.values(fieldError.types).flatMap((v: any) =>
                    Array.isArray(v) ? v.map(String) : [String(v)],
                );
            }
            if (fieldError.message != null) return [String(fieldError.message)];
            return [];
        };
        const pushLabeled = (label: string, fieldError: any) => {
            const msgs = extractMessages(fieldError);
            if (msgs.length === 0) {
                messages.push(label);
                return;
            }
            msgs.forEach((m) => messages.push(`${label}: ${m}`));
        };
        for (const [key, error] of Object.entries(errors)) {
            if (key === "root" && typeof error === "object") {
                // Root errors (e.g. server-side non-field errors) have no
                // field label — render their messages as-is.
                for (const rootError of Object.values(
                    error as Record<string, any>,
                )) {
                    extractMessages(rootError).forEach((m) => messages.push(m));
                }
            } else if (key === "formData" && typeof error === "object") {
                for (const [fieldName, fieldError] of Object.entries(
                    error as Record<string, any>,
                )) {
                    pushLabeled(
                        formFieldLabels[fieldName] || fieldName,
                        fieldError,
                    );
                }
            } else {
                pushLabeled(FIELD_LABELS[key] || key, error);
            }
        }
        return messages;
    })();

    if (errorMessages.length === 0) {
        return null;
    }

    return (
        <div className="my-3 p-3 bg-red-50 border border-red-100 rounded-md">
            <div className="flex items-center gap-2 text-red-600 font-medium text-sm mb-1">
                <FontAwesomeIcon icon={faTriangleExclamation} />
                Please fix the following errors:
            </div>
            <ul className="list-disc list-inside text-red-500 text-sm">
                {errorMessages.map((msg, i) => (
                    <li key={`${i}-${msg}`}>{msg}</li>
                ))}
            </ul>
        </div>
    );
};
